import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Space,
  Card,
  Tag,
  DatePicker,
  Input,
  Divider,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  PrinterOutlined,
  CheckCircleOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import {
  plantillaConceptos,
  calcularTotales,
  MAPA_BONIFICACION_CONCEPTO,
  buildBoletaHtml,
  exportarBoletaPDF,
} from '../utils/boletaPago';

const { TextArea } = Input;
const { Option } = Select;

const TIPOS_BONIFICACION = Object.keys(MAPA_BONIFICACION_CONCEPTO);

const LiquidacionPagos = () => {
  const { user } = useAuth();
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [loading, setLoading] = useState(false);
  const isMainAdmin = user && user.rol === 'admin' && !user.empresa_id;
  const [modalVisible, setModalVisible] = useState(false);
  const [boletaModalVisible, setBoletaModalVisible] = useState(false);
  const [editingLiquidacion, setEditingLiquidacion] = useState(null);
  const [selectedLiquidacion, setSelectedLiquidacion] = useState(null);
  const [calculando, setCalculando] = useState(false);
  const [exportandoPdf, setExportandoPdf] = useState(false);
  const [datosCalculo, setDatosCalculo] = useState(null);
  const [conceptos, setConceptos] = useState(plantillaConceptos());
  const [bonificaciones, setBonificaciones] = useState([]);
  const [form] = Form.useForm();

  const totales = useMemo(() => calcularTotales(conceptos), [conceptos]);

  useEffect(() => {
    fetchLiquidaciones();
    fetchPersonal();
  }, []);

  const fetchLiquidaciones = async () => {
    setLoading(true);
    try {
      const response = await api.get('/liquidacion-pagos');
      setLiquidaciones(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      message.error('Error al cargar liquidaciones');
      setLiquidaciones([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPersonal = async () => {
    try {
      const response = await api.get('/personal');
      setPersonal(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error al cargar personal');
    }
  };

  const aplicarConceptosCalculo = (data) => {
    const plantilla = plantillaConceptos(data.conceptos || []);
    setConceptos(plantilla);
    const calc = calcularTotales(plantilla);
    form.setFieldsValue({
      monto_base: calc.montoBase,
      monto_bonificaciones: calc.bonificaciones,
      monto_descuentos: calc.descuentos,
      dias_laborados: data.dias_laborados ?? 0,
      horas_laboradas: data.horas_laboradas ?? 0,
      horas_extras: data.horas_extras ?? 0,
      dias_subsidiados: data.dias_subsidiados ?? 0,
    });
  };

  const handleCalcular = async (personalId, mes, anio) => {
    setCalculando(true);
    try {
      const response = await api.get(`/liquidacion-pagos/calcular/${personalId}?mes=${mes}&anio=${anio}`);
      setDatosCalculo(response.data);
      form.setFieldsValue({
        personal_id: personalId,
        periodo_mes: mes,
        periodo_anio: anio,
        fecha_liquidacion: dayjs(),
      });
      aplicarConceptosCalculo(response.data);
      setBonificaciones([]);
      message.success('Cálculo realizado exitosamente');
    } catch (error) {
      message.error(error.message);
    } finally {
      setCalculando(false);
    }
  };

  const handleConceptoChange = (codigo, value) => {
    setConceptos((prev) => {
      const next = prev.map((c) =>
        c.codigo === codigo ? { ...c, monto: parseFloat(value || 0) } : c
      );
      const calc = calcularTotales(next);
      form.setFieldsValue({
        monto_base: calc.montoBase,
        monto_bonificaciones: calc.bonificaciones,
        monto_descuentos: calc.descuentos,
      });
      return next;
    });
  };

  const handleSubmit = async (values) => {
    try {
      const calc = calcularTotales(conceptos);
      const data = {
        ...values,
        periodo_mes: parseInt(values.periodo_mes, 10),
        periodo_anio: parseInt(values.periodo_anio, 10),
        fecha_liquidacion: values.fecha_liquidacion.format('YYYY-MM-DD'),
        detalles: datosCalculo?.detalles || [],
        conceptos,
        bonificaciones,
        monto_base: calc.montoBase,
        monto_bonificaciones: calc.bonificaciones,
        monto_descuentos: calc.descuentos,
        dias_laborados: values.dias_laborados ?? 0,
        horas_laboradas: values.horas_laboradas ?? 0,
        horas_extras: values.horas_extras ?? 0,
        dias_subsidiados: values.dias_subsidiados ?? 0,
      };

      if (editingLiquidacion) {
        await api.put(`/liquidacion-pagos/${editingLiquidacion.id}`, data);
        message.success('Liquidación actualizada exitosamente');
      } else {
        const response = await api.post('/liquidacion-pagos', data);
        message.success('Liquidación creada exitosamente');
        if (response.data.numero_boleta) {
          message.info(`Número de boleta: ${response.data.numero_boleta}`);
        }
      }
      setModalVisible(false);
      form.resetFields();
      setEditingLiquidacion(null);
      setDatosCalculo(null);
      setConceptos(plantillaConceptos());
      setBonificaciones([]);
      fetchLiquidaciones();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleEdit = async (record) => {
    try {
      const response = await api.get(`/liquidacion-pagos/${record.id}`);
      const liquidacion = response.data;

      if (!liquidacion || !liquidacion.id) {
        message.error('No se encontraron datos de la liquidación');
        return;
      }

      setEditingLiquidacion(liquidacion);
      const plantilla = plantillaConceptos(liquidacion.conceptos || []);
      setConceptos(plantilla);
      setBonificaciones(liquidacion.bonificaciones || []);

      form.setFieldsValue({
        personal_id: liquidacion.personal_id,
        periodo_mes: liquidacion.periodo_mes,
        periodo_anio: liquidacion.periodo_anio,
        monto_base: liquidacion.monto_base,
        monto_bonificaciones: liquidacion.monto_bonificaciones || 0,
        monto_descuentos: liquidacion.monto_descuentos || 0,
        dias_laborados: liquidacion.dias_laborados ?? 0,
        horas_laboradas: liquidacion.horas_laboradas ?? 0,
        horas_extras: liquidacion.horas_extras ?? 0,
        dias_subsidiados: liquidacion.dias_subsidiados ?? 0,
        fecha_liquidacion: liquidacion.fecha_liquidacion ? dayjs(liquidacion.fecha_liquidacion) : dayjs(),
        fecha_pago: liquidacion.fecha_pago ? dayjs(liquidacion.fecha_pago) : null,
        estado: liquidacion.estado,
        observaciones: liquidacion.observaciones,
      });

      if (liquidacion.detalles && liquidacion.detalles.length > 0) {
        setDatosCalculo({
          personal: { tipo_pago: liquidacion.tipo_pago },
          monto_base: liquidacion.monto_base,
          detalles: liquidacion.detalles,
        });
      }

      setModalVisible(true);
    } catch (error) {
      console.error('Error al cargar liquidación:', error);
      message.error('Error al cargar liquidación: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleVerBoleta = async (record) => {
    try {
      const response = await api.get(`/liquidacion-pagos/${record.id}`);
      const liquidacion = response.data;
      if (!liquidacion || !liquidacion.id) {
        message.error('No se encontraron datos de la boleta');
        return;
      }
      setSelectedLiquidacion({
        ...liquidacion,
        conceptos: plantillaConceptos(liquidacion.conceptos || []),
      });
      setBoletaModalVisible(true);
    } catch (error) {
      message.error('Error al cargar boleta: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleExportarPDF = async () => {
    if (!selectedLiquidacion) return;
    setExportandoPdf(true);
    try {
      await exportarBoletaPDF(selectedLiquidacion);
      message.success('PDF generado exitosamente');
    } catch (error) {
      console.error(error);
      message.error('Error al generar PDF');
    } finally {
      setExportandoPdf(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/liquidacion-pagos/${id}`);
      message.success('Liquidación eliminada exitosamente');
      fetchLiquidaciones();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleAprobar = async (record) => {
    try {
      await api.put(`/liquidacion-pagos/${record.id}`, {
        estado: 'pagado',
        fecha_pago: dayjs().format('YYYY-MM-DD'),
      });
      message.success('Liquidación aprobada y cerrada exitosamente');
      fetchLiquidaciones();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleAgregarBonificacion = () => {
    setBonificaciones([...bonificaciones, { descripcion: '', monto: 0 }]);
  };

  const handleEliminarBonificacion = (index) => {
    const nuevas = bonificaciones.filter((_, i) => i !== index);
    setBonificaciones(nuevas);
    sincronizarBonificacionesEnConceptos(nuevas);
  };

  const sincronizarBonificacionesEnConceptos = (lista) => {
    const agregados = {};
    lista.forEach((b) => {
      const codigo = MAPA_BONIFICACION_CONCEPTO[b.descripcion];
      if (!codigo) return;
      agregados[codigo] = (agregados[codigo] || 0) + parseFloat(b.monto || 0);
    });

    setConceptos((prev) => {
      const next = prev.map((c) => {
        if (Object.prototype.hasOwnProperty.call(agregados, c.codigo)) {
          return { ...c, monto: agregados[c.codigo] };
        }
        // No resetear conceptos no mapeados desde bonificaciones
        return c;
      });
      const calc = calcularTotales(next);
      form.setFieldsValue({
        monto_base: calc.montoBase,
        monto_bonificaciones: calc.bonificaciones,
        monto_descuentos: calc.descuentos,
      });
      return next;
    });
  };

  const handleBonificacionChange = (index, field, value) => {
    const nuevas = [...bonificaciones];
    nuevas[index][field] = value;
    setBonificaciones(nuevas);
    sincronizarBonificacionesEnConceptos(nuevas);
  };

  const columns = [
    {
      title: 'Número Boleta',
      dataIndex: 'numero_boleta',
      key: 'numero_boleta',
    },
    {
      title: 'Personal',
      key: 'personal',
      render: (_, record) => record.nombre_completo || '-',
    },
    ...(isMainAdmin
      ? [{
          title: 'Empresa',
          dataIndex: 'empresa_nombre',
          key: 'empresa_nombre',
        }]
      : []),
    {
      title: 'Periodo',
      key: 'periodo',
      render: (_, record) => `${record.periodo_mes}/${record.periodo_anio}`,
    },
    {
      title: 'Monto Base',
      dataIndex: 'monto_base',
      key: 'monto_base',
      render: (monto) => `S/ ${parseFloat(monto || 0).toFixed(2)}`,
    },
    {
      title: 'Bonificaciones',
      dataIndex: 'monto_bonificaciones',
      key: 'monto_bonificaciones',
      render: (monto) => `S/ ${parseFloat(monto || 0).toFixed(2)}`,
    },
    {
      title: 'Total',
      dataIndex: 'monto_total',
      key: 'monto_total',
      render: (total) => <strong>S/ {parseFloat(total || 0).toFixed(2)}</strong>,
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => {
        const colors = {
          pendiente: 'orange',
          liquidado: 'blue',
          pagado: 'green',
          cancelado: 'red',
        };
        return <Tag color={colors[estado]}>{estado === 'cancelado' ? 'ANULADO' : estado.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Fecha Liquidación',
      dataIndex: 'fecha_liquidacion',
      key: 'fecha_liquidacion',
      render: (fecha) => (fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Space wrap>
          <Button type="primary" icon={<PrinterOutlined />} onClick={() => handleVerBoleta(record)}>
            Ver Boleta
          </Button>
          {record.estado !== 'cancelado' && (
            <>
              <Button type="default" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
                Editar
              </Button>
              {record.estado !== 'pagado' && (
                <Popconfirm
                  title="¿Estás seguro de aprobar y cerrar esta liquidación?"
                  description="Esta acción marcará la liquidación como pagada."
                  onConfirm={() => handleAprobar(record)}
                  okText="Sí, Aprobar"
                  cancelText="Cancelar"
                >
                  <Button type="primary" icon={<CheckCircleOutlined />} style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>
                    Aprobar
                  </Button>
                </Popconfirm>
              )}
            </>
          )}
          <Popconfirm
            title="¿Estás seguro de eliminar esta liquidación?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const meses = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' },
  ];

  const renderConceptoInputs = (grupo) =>
    conceptos
      .filter((c) => c.grupo === grupo)
      .map((c) => (
        <div key={c.codigo} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
          <span style={{ flex: 1, fontSize: 12 }}>{c.descripcion}</span>
          <InputNumber
            min={0}
            precision={2}
            value={c.monto}
            onChange={(value) => handleConceptoChange(c.codigo, value)}
            style={{ width: 110 }}
          />
        </div>
      ));

  const resetFormState = () => {
    setModalVisible(false);
    form.resetFields();
    setEditingLiquidacion(null);
    setDatosCalculo(null);
    setConceptos(plantillaConceptos());
    setBonificaciones([]);
  };

  return (
    <div>
      <Card
        title="Liquidación de Pagos a Personal"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingLiquidacion(null);
              setDatosCalculo(null);
              setConceptos(plantillaConceptos());
              setBonificaciones([]);
              form.resetFields();
              form.setFieldsValue({
                periodo_mes: dayjs().month() + 1,
                periodo_anio: dayjs().year(),
                fecha_liquidacion: dayjs(),
                dias_laborados: 0,
                horas_laboradas: 0,
                horas_extras: 0,
                dias_subsidiados: 0,
              });
              setModalVisible(true);
            }}
          >
            Nueva Liquidación
          </Button>
        }
      >
        <Table columns={columns} dataSource={liquidaciones} rowKey="id" loading={loading} />
      </Card>

      <Modal
        title={editingLiquidacion ? 'Editar Liquidación' : 'Nueva Liquidación'}
        open={modalVisible}
        onCancel={resetFormState}
        footer={null}
        width={1100}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="personal_id"
                label="Personal"
                rules={[{ required: true, message: 'Selecciona un personal' }]}
              >
                <Select
                  placeholder="Selecciona un personal"
                  disabled={!!editingLiquidacion}
                  onChange={(value) => {
                    const mes = form.getFieldValue('periodo_mes') || dayjs().month() + 1;
                    const anio = form.getFieldValue('periodo_anio') || dayjs().year();
                    if (value) handleCalcular(value, mes, anio);
                  }}
                >
                  {personal.map((p) => (
                    <Option key={p.id} value={p.id}>
                      {p.nombre_completo} - {p.cargo}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="periodo_mes" label="Mes" rules={[{ required: true, message: 'Selecciona el mes' }]}>
                <Select
                  placeholder="Mes"
                  disabled={!!editingLiquidacion}
                  onChange={(value) => {
                    const personalId = form.getFieldValue('personal_id');
                    const anio = form.getFieldValue('periodo_anio') || dayjs().year();
                    if (personalId) handleCalcular(personalId, value, anio);
                  }}
                >
                  {meses.map((m) => (
                    <Option key={m.value} value={m.value}>
                      {m.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="periodo_anio" label="Año" rules={[{ required: true, message: 'Selecciona el año' }]}>
                <InputNumber
                  min={2020}
                  max={2100}
                  style={{ width: '100%' }}
                  disabled={!!editingLiquidacion}
                  onChange={(value) => {
                    const personalId = form.getFieldValue('personal_id');
                    const mes = form.getFieldValue('periodo_mes') || dayjs().month() + 1;
                    if (personalId && value) handleCalcular(personalId, mes, value);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          {calculando && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <p>Calculando liquidación...</p>
            </div>
          )}

          {datosCalculo && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic title="Monto Base" value={datosCalculo.monto_base} prefix={<DollarOutlined />} precision={2} />
                </Col>
                <Col span={8}>
                  <Statistic title="Servicios" value={datosCalculo.detalles?.length || 0} />
                </Col>
                <Col span={8}>
                  <Statistic title="Tipo de Pago" value={datosCalculo.personal?.tipo_pago || '-'} />
                </Col>
              </Row>
            </Card>
          )}

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="dias_laborados" label="Días Laborados">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="horas_laboradas" label="Horas Laboradas">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="horas_extras" label="Horas Extras">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="dias_subsidiados" label="Días Subsidiados">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Conceptos de Boleta (editables)</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Card size="small" title="Ingresos" style={{ marginBottom: 12 }}>
                {renderConceptoInputs('ingreso')}
                <Divider style={{ margin: '8px 0' }} />
                <strong>Total: S/ {totales.ingresos.toFixed(2)}</strong>
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" title="Retenciones y Descuentos" style={{ marginBottom: 12 }}>
                {renderConceptoInputs('descuento')}
                <Divider style={{ margin: '8px 0' }} />
                <strong>Total: S/ {totales.descuentos.toFixed(2)}</strong>
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" title="Aportes del Empleador" style={{ marginBottom: 12 }}>
                {renderConceptoInputs('aporte')}
                <Divider style={{ margin: '8px 0' }} />
                <strong>EsSalud: S/ {totales.aportes.toFixed(2)}</strong>
                <div style={{ marginTop: 12, fontSize: 16 }}>
                  <strong>Total a Pagar: S/ {totales.totalPagar.toFixed(2)}</strong>
                </div>
              </Card>
            </Col>
          </Row>

          <Form.Item name="monto_base" hidden>
            <InputNumber />
          </Form.Item>
          <Form.Item name="monto_bonificaciones" hidden>
            <InputNumber />
          </Form.Item>
          <Form.Item name="monto_descuentos" hidden>
            <InputNumber />
          </Form.Item>

          <Divider>Atajos de Bonificaciones</Divider>
          <Button type="dashed" onClick={handleAgregarBonificacion} style={{ width: '100%', marginBottom: 16 }}>
            Agregar Bonificación
          </Button>
          {bonificaciones.map((bonif, index) => (
            <Row gutter={16} key={index} style={{ marginBottom: 8 }}>
              <Col span={16}>
                <Select
                  placeholder="Selecciona una bonificación"
                  value={bonif.descripcion || undefined}
                  onChange={(value) => handleBonificacionChange(index, 'descripcion', value)}
                  style={{ width: '100%' }}
                >
                  {TIPOS_BONIFICACION.map((tipo) => (
                    <Option key={tipo} value={tipo}>
                      {tipo}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={6}>
                <InputNumber
                  placeholder="Monto"
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  value={bonif.monto}
                  onChange={(value) => handleBonificacionChange(index, 'monto', value)}
                />
              </Col>
              <Col span={2}>
                <Button type="primary" danger onClick={() => handleEliminarBonificacion(index)}>
                  X
                </Button>
              </Col>
            </Row>
          ))}

          <Form.Item
            name="fecha_liquidacion"
            label="Fecha de Liquidación"
            rules={[{ required: true, message: 'Selecciona la fecha' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="observaciones" label="Observaciones">
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={calculando}>
                {editingLiquidacion ? 'Actualizar' : 'Crear Liquidación'}
              </Button>
              <Button onClick={resetFormState}>Cancelar</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Boleta de Pago - ${selectedLiquidacion?.numero_boleta || ''}`}
        open={boletaModalVisible}
        onCancel={() => {
          setBoletaModalVisible(false);
          setSelectedLiquidacion(null);
        }}
        footer={[
          <Button
            key="pdf"
            type="primary"
            icon={<FilePdfOutlined />}
            loading={exportandoPdf}
            onClick={handleExportarPDF}
          >
            Exportar PDF
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setBoletaModalVisible(false);
              setSelectedLiquidacion(null);
            }}
          >
            Cerrar
          </Button>,
        ]}
        width={1080}
        style={{ top: 20 }}
      >
        {selectedLiquidacion && (
          <div
            style={{ maxHeight: '75vh', overflow: 'auto', background: '#f0f0f0', padding: 12 }}
            dangerouslySetInnerHTML={{ __html: buildBoletaHtml(selectedLiquidacion) }}
          />
        )}
      </Modal>
    </div>
  );
};

export default LiquidacionPagos;
